import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  Input,
  Select,
  Textarea,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Card,
  CardBody,
  CardHeader,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useToast,
  useColorModeValue,
  SimpleGrid,
  Badge,
  Tag,
  TagLabel,
  IconButton,
  Divider,
  FormControl,
  FormLabel,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  HStack,
  VStack,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Progress,
  Switch,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import {
  SearchIcon,
  CalendarIcon,
  DownloadIcon,
  AddIcon,
  ChevronDownIcon,
  CloseIcon,
  CheckCircleIcon,
  TimeIcon,
  WarningIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EditIcon,
  ViewIcon,
  DeleteIcon,
  AttachmentIcon,
  EmailIcon,
  PhoneIcon,
  ChatIcon,
  StarIcon,
  SettingsIcon,
} from '@chakra-ui/icons';
import {
  FaFolder,
  FaChartBar,
  FaUsers,
  FaDatabase,
  FaShieldAlt,
  FaFilePdf,
  FaFileExcel,
  FaFileWord,
  FaBell,
} from 'react-icons/fa';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useLocation, useNavigate } from 'react-router-dom';

const StatusPill = ({ status }) => {
  const statusLower = status?.toLowerCase() || '';
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
      case 'pending review':
        return { bg: 'orange.100', color: 'orange.800', label: 'Pending' };
      case 'in progress':
      case 'in-progress':
      case 'working':
        return { bg: 'blue.100', color: 'blue.800', label: 'In Progress' };
      case 'resolved':
      case 'completed':
      case 'done':
        return { bg: 'green.100', color: 'green.800', label: 'Resolved' };
      case 'rejected':
      case 'declined':
        return { bg: 'red.100', color: 'red.800', label: 'Rejected' };
      default:
        return {
          bg: 'gray.100',
          color: 'gray.800',
          label: status || 'Unknown',
        };
    }
  };

  const colors = getStatusColor(statusLower);

  return (
    <Badge
      bg={colors.bg}
      color={colors.color}
      px={3}
      py={1}
      borderRadius="full"
      fontSize="xs"
      fontWeight="medium"
    >
      {colors.label}
    </Badge>
  );
};

const PriorityBadge = ({ priority }) => {
  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical':
        return { bg: 'red.100', color: 'red.800', label: 'Critical' };
      case 'high':
        return { bg: 'orange.100', color: 'orange.800', label: 'High' };
      case 'medium':
        return { bg: 'yellow.100', color: 'yellow.800', label: 'Medium' };
      case 'low':
        return { bg: 'green.100', color: 'green.800', label: 'Low' };
      default:
        return { bg: 'gray.100', color: 'gray.800', label: priority || 'Low' };
    }
  };

  const colors = getPriorityColor(priority);

  return (
    <Badge
      bg={colors.bg}
      color={colors.color}
      px={2}
      py={1}
      borderRadius="md"
      fontSize="xs"
      fontWeight="medium"
    >
      {colors.label}
    </Badge>
  );
};

export default function TechSupportDashboard() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedKPIModal, setSelectedKPIModal] = useState(null);
  const [filteredKPIQueries, setFilteredKPIQueries] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    project: 'DMO Project',
    status: 'Pending',
    priority: 'medium',
    description: '',
    assignedString: '',
    uploadfile: '',
    uploadfilename: '',
    tag: '',
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [backendOnline, setBackendOnline] = useState(false);
  const [refreshIntervalSec, setRefreshIntervalSec] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [reportFromDate, setReportFromDate] = useState('');
  const [reportToDate, setReportToDate] = useState('');
  const [viewQuery, setViewQuery] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const {
    isOpen: isViewModalOpen,
    onOpen: onViewModalOpen,
    onClose: onViewModalClose,
  } = useDisclosure();
  const {
    isOpen: isKPIModalOpen,
    onOpen: onKPIModalOpen,
    onClose: onKPIModalClose,
  } = useDisclosure();

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const subtleTextColor = useColorModeValue('gray.600', 'gray.400');
  const headerBg = useColorModeValue('white', 'gray.800');
  const inputBg = useColorModeValue('white', 'gray.700');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const primaryColor = useColorModeValue('blue.600', 'blue.300');
  const successColor = useColorModeValue('green.500', 'green.300');
  const warningColor = useColorModeValue('orange.500', 'orange.300');
  const dangerColor = useColorModeValue('red.500', 'red.300');

  const fetchQueries = async (retries = 2, delayMs = 800) => {
    setLoading(true);
    setError(null);
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const response = await fetch('http://localhost:5001/api/queries');
        if (!response.ok)
          throw new Error(`Failed to fetch queries (${response.status})`);
        const queries = await response.json();
        const normalized = queries.map((q) => ({
          ...q,
          displayDate: q.createdOn
            ? new Date(q.createdOn).toLocaleString()
            : '',
        }));
        setData(normalized);
        setError(null);
        setLoading(false);
        return normalized;
      } catch (err) {
        console.error('Error fetching queries (attempt', attempt, '):', err);
        setError(err.message || 'Failed to fetch');
        if (attempt < retries) await new Promise((r) => setTimeout(r, delayMs));
      }
    }
    setLoading(false);
    return null;
  };

  const fetchHealth = async () => {
    try {
      const r = await fetch('http://localhost:5001/health');
      if (!r.ok) throw new Error('unhealthy');
      const json = await r.json();
      setBackendOnline(
        !!json &&
          (json.status === 'Server is running' ||
            json.status === 'Server is running'),
      );
    } catch (err) {
      setBackendOnline(false);
    }
  };

  useEffect(() => {
    fetchQueries();
  }, []);

  useEffect(() => {
    fetchHealth();
    const id = setInterval(fetchHealth, 10000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!refreshIntervalSec || refreshIntervalSec <= 0) return undefined;
    const id = setInterval(() => {
      fetchQueries();
    }, refreshIntervalSec * 1000);
    return () => clearInterval(id);
  }, [refreshIntervalSec]);

  const overallCount = data.length;
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const monthlyCount = data.filter((d) => {
    if (!d.createdOn) return false;
    try {
      const queryDate = new Date(d.createdOn);
      return (
        queryDate.getFullYear() === currentYear &&
        queryDate.getMonth() === currentMonth
      );
    } catch {
      return false;
    }
  }).length;

  const pendingCount = data.filter(
    (d) => (d.status || '').toLowerCase() === 'pending',
  ).length;
  const inProgressCount = data.filter((d) => {
    const s = (d.status || '').toLowerCase();
    return (
      s === 'in progress' ||
      s === 'in-progress' ||
      s === 'progress' ||
      s === 'working'
    );
  }).length;
  const resolvedCount = data.filter(
    (d) => (d.status || '').toLowerCase() === 'resolved',
  ).length;
  const rejectedCount = data.filter(
    (d) => (d.status || '').toLowerCase() === 'rejected',
  ).length;

  const kpiCards = [
    {
      title: 'Overall Queries',
      value: overallCount,
      description: 'All queries combined',
      icon: FaFolder,
      color: 'blue.500',
      type: 'overall',
    },
    {
      title: 'Monthly Queries',
      value: monthlyCount,
      description: 'Current month queries',
      icon: FaChartBar,
      color: 'purple.500',
      type: 'monthly',
    },
    {
      title: 'Pending Queries',
      value: pendingCount,
      description: 'Queries still pending',
      icon: TimeIcon,
      color: 'orange.500',
      type: 'pending',
    },
    {
      title: 'In Progress',
      value: inProgressCount,
      description: 'Currently being worked on',
      icon: SettingsIcon,
      color: 'yellow.500',
      type: 'inProgress',
    },
    {
      title: 'Resolved',
      value: resolvedCount,
      description: 'Successfully closed',
      icon: CheckCircleIcon,
      color: 'green.500',
      type: 'resolved',
    },
    {
      title: 'Rejected',
      value: rejectedCount,
      description: 'Declined queries',
      icon: WarningIcon,
      color: 'red.500',
      type: 'rejected',
    },
  ];

  const handleFormChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setFormErrors((f) => ({ ...f, [field]: null }));
  };

  const handleAddClick = () => {
    setShowAddModal(true);
  };

  const handleSubmitQuery = async () => {
    const errors = {};
    if (!formData.title || !formData.title.toString().trim())
      errors.title = 'Title is required';
    if (!formData.project || !formData.project.toString().trim())
      errors.project = 'Project is required';
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      const newQuery = {
        title: formData.title,
        project: formData.project || 'Unassigned',
        status: formData.status,
        priority: formData.priority,
        description: formData.description || '',
        assigned: formData.assignedString
          ? formData.assignedString
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
        uploadfile: formData.uploadfile || '',
      };

      const response = await fetch('http://localhost:5001/api/queries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newQuery),
      });

      if (!response.ok) throw new Error('Failed to save query');

      const savedQuery = await response.json();
      const normalized = {
        ...savedQuery,
        displayDate: savedQuery.createdOn
          ? new Date(savedQuery.createdOn).toLocaleString()
          : new Date().toLocaleString(),
      };

      setData((d) => [normalized, ...d]);
      fetchQueries().catch(() => {});

      setFormData({
        title: '',
        project: 'DMO Project',
        status: 'Pending',
        priority: 'medium',
        description: '',
        assignedString: '',
        uploadfile: '',
        uploadfilename: '',
        tag: '',
      });
      setShowAddModal(false);

      toast({
        title: 'Query Saved',
        description: 'Your query has been successfully submitted.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      try {
        const localFallback = {
          _id: `local-${Date.now()}`,
          title: formData.title,
          project: formData.project || 'Unassigned',
          status: formData.status,
          priority: formData.priority,
          description: formData.description || '',
          assigned: formData.assignedString
            ? formData.assignedString
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
            : [],
          uploadfile: formData.uploadfile || '',
          uploadfilename: formData.uploadfilename || '',
          tag: formData.tag || '',
          displayDate: new Date().toLocaleString(),
        };
        setData((d) => [localFallback, ...d]);
        setFormData({
          title: '',
          project: 'DMO Project',
          status: 'Pending',
          priority: 'medium',
          description: '',
          assignedString: '',
          uploadfile: '',
          uploadfilename: '',
          tag: '',
        });
        setShowAddModal(false);

        toast({
          title: 'Saved Locally',
          description: 'Query saved locally (server unreachable).',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
      } catch (e) {
        console.error('Failed to add locally:', e);
        toast({
          title: 'Error',
          description: 'Failed to save query. Make sure the server is running.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleKPICardClick = (type) => {
    let filtered = [];

    if (type === 'overall') {
      filtered = data;
    } else if (type === 'monthly') {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();
      filtered = data.filter((q) => {
        if (!q.createdOn) return false;
        const parsed = new Date(q.createdOn);
        if (!isNaN(parsed.getTime())) {
          return (
            parsed.getFullYear() === currentYear &&
            parsed.getMonth() === currentMonth
          );
        }
        return false;
      });
    } else if (type === 'pending') {
      filtered = data.filter((q) => q.status?.toLowerCase() === 'pending');
    } else if (type === 'inProgress') {
      filtered = data.filter(
        (q) =>
          q.status?.toLowerCase() === 'in progress' ||
          q.status?.toLowerCase() === 'in-progress' ||
          q.status?.toLowerCase() === 'working',
      );
    } else if (type === 'resolved') {
      filtered = data.filter(
        (q) =>
          q.status?.toLowerCase() === 'resolved' ||
          q.status?.toLowerCase() === 'completed',
      );
    } else if (type === 'rejected') {
      filtered = data.filter(
        (q) =>
          q.status?.toLowerCase() === 'rejected' ||
          q.status?.toLowerCase() === 'declined',
      );
    }

    setFilteredKPIQueries(filtered);
    setSelectedKPIModal(type);
    onKPIModalOpen();
  };

  const handleViewQuery = (query) => {
    setViewQuery(query);
    onViewModalOpen();
  };

  const downloadPDF = () => {
    const exportData = getExportData();
    if (!exportData || exportData.length === 0) {
      toast({
        title: 'No Data',
        description: 'No queries match the selected filters',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text('Query Summary Report', 20, 20);
      doc.setFontSize(10);
      if (reportFromDate || reportToDate) {
        doc.text(
          `From: ${reportFromDate || '-'} | To: ${reportToDate || '-'}`,
          20,
          30,
        );
      }
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 20, 40);

      const tableData = exportData.map((q) => [
        q.title,
        q.project,
        q.status,
        q.priority,
        q.displayDate || q.created,
        (q.assigned || []).join(', '),
      ]);

      const autoTableOptions = {
        head: [
          ['Title', 'Project', 'Status', 'Priority', 'Created', 'Assigned To'],
        ],
        body: tableData,
        startY: 50,
      };

      doc.autoTable(autoTableOptions);
      doc.save(`query-report-${new Date().toISOString().slice(0, 10)}.pdf`);

      toast({
        title: 'PDF Exported',
        description: 'Your report has been downloaded as PDF',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error('PDF export error:', err);
      toast({
        title: 'Export Error',
        description: 'Failed to export PDF',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const downloadExcel = () => {
    const exportData = getExportData();
    if (!exportData || exportData.length === 0) {
      toast({
        title: 'No Data',
        description: 'No queries match the selected filters',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const ws_data = [
        ['Query Summary Report'],
        [`From: ${reportFromDate || '-'} | To: ${reportToDate || '-'}`],
        [`Generated on: ${new Date().toLocaleString()}`],
        [],
        ['Title', 'Project', 'Status', 'Priority', 'Created', 'Assigned To'],
        ...exportData.map((q) => [
          q.title,
          q.project,
          q.status,
          q.priority,
          q.displayDate || q.created,
          (q.assigned || []).join(', '),
        ]),
      ];

      const ws = XLSX.utils.aoa_to_sheet(ws_data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Queries');
      XLSX.writeFile(
        wb,
        `query-report-${new Date().toISOString().slice(0, 10)}.xlsx`,
      );

      toast({
        title: 'Excel Exported',
        description: 'Your report has been downloaded as Excel',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error('Excel export error:', err);
      toast({
        title: 'Export Error',
        description: 'Failed to export Excel',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const downloadWord = () => {
    const exportData = getExportData();
    if (!exportData || exportData.length === 0) {
      toast({
        title: 'No Data',
        description: 'No queries match the selected filters',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      let content = 'Query Summary Report\n';
      content += `From: ${reportFromDate || '-'} | To: ${
        reportToDate || '-'
      }\n`;
      content += `Generated on: ${new Date().toLocaleString()}\n\n`;

      exportData.forEach((q, idx) => {
        content += `${idx + 1}. ${q.title}\n`;
        content += `   Project: ${q.project || 'N/A'}\n`;
        content += `   Status: ${q.status || 'N/A'}\n`;
        content += `   Priority: ${q.priority || 'N/A'}\n`;
        content += `   Created: ${q.displayDate || q.created || 'N/A'}\n`;
        content += `   Assigned To: ${
          Array.isArray(q.assigned)
            ? q.assigned.join(', ')
            : q.assigned || 'N/A'
        }\n`;
        content += '-'.repeat(50) + '\n';
      });

      const blob = new Blob([content], {
        type: 'application/msword;charset=utf-8',
      });
      saveAs(blob, `query-report-${new Date().toISOString().slice(0, 10)}.doc`);

      toast({
        title: 'Word Exported',
        description: 'Your report has been downloaded as Word',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error('Word export error:', err);
      toast({
        title: 'Export Error',
        description: 'Failed to export Word',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getExportData = () => {
    if (viewQuery) return [viewQuery];

    if (reportFromDate && reportToDate) {
      const from = new Date(reportFromDate);
      const to = new Date(reportToDate);
      to.setHours(23, 59, 59, 999);

      return data.filter((q) => {
        let dateStr = q.createdOn || q.created || q.displayDate;
        if (!dateStr) return false;
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return false;
        return d >= from && d <= to;
      });
    }

    return data;
  };

  const filteredData = data.filter((row) => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      row.title?.toLowerCase().includes(search) ||
      row.project?.toLowerCase().includes(search) ||
      row.description?.toLowerCase().includes(search)
    );
  });

  return (
    <Box pt={'100px'} bg={bgColor} minH="100vh">
      <Box
        bg={headerBg}
        borderBottom="1px"
        borderColor={cardBorder}
        px={6}
        py={4}
      >
        <Flex justify="space-between" align="center">
          <Flex align="center" gap={4}>
            <Box
              w={12}
              h={12}
              borderRadius="lg"
              bg={primaryColor}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={FaShieldAlt} color="white" boxSize={6} />
            </Box>
            <Box>
              <Heading size="lg" color={textColor}>
                TechSupport Portal
              </Heading>
              <Text color={subtleTextColor} fontSize="sm">
                User Support Dashboard
              </Text>
            </Box>
          </Flex>

          <Flex align="center" gap={4}>
            <Badge
              colorScheme={backendOnline ? 'green' : 'red'}
              px={3}
              py={1}
              borderRadius="full"
              fontSize="sm"
            >
              {backendOnline ? '🟢 Backend Online' : '🔴 Backend Offline'}
            </Badge>
            <Button
              leftIcon={<AddIcon />}
              colorScheme="blue"
              onClick={handleAddClick}
              size="sm"
            >
              New Query
            </Button>
          </Flex>
        </Flex>
      </Box>

      <Box p={6}>
        <SimpleGrid columns={{ base: 1, lg: 4 }} spacing={6}>
          <Box gridColumn={{ base: '1', lg: '1 / span 3' }}>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} mb={6}>
              {kpiCards.map((card) => (
                <Card
                  key={card.type}
                  bg={cardBg}
                  border="1px"
                  borderColor={cardBorder}
                  cursor="pointer"
                  onClick={() => handleKPICardClick(card.type)}
                  transition="all 0.2s"
                  _hover={{ transform: 'translateY(-2px)', shadow: 'md' }}
                >
                  <CardBody>
                    <Flex justify="space-between" align="center">
                      <Box>
                        <Text color={subtleTextColor} fontSize="sm" mb={1}>
                          {card.title}
                        </Text>
                        <Heading size="lg" color={textColor}>
                          {card.value}
                        </Heading>
                        <Text color={subtleTextColor} fontSize="xs" mt={2}>
                          {card.description}
                        </Text>
                      </Box>
                      <Box
                        w={12}
                        h={12}
                        borderRadius="lg"
                        bg={`${card.color.replace('.', '')}.100`}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Icon as={card.icon} color={card.color} boxSize={6} />
                      </Box>
                    </Flex>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>

            <Card bg={cardBg} border="1px" borderColor={cardBorder} mb={6}>
              <CardBody>
                <Flex justify="space-between" align="center" mb={4}>
                  <Heading size="md" color={textColor}>
                    Query Management
                  </Heading>
                  <Text color={subtleTextColor}>
                    Total Queries: {data.length}
                  </Text>
                </Flex>

                <InputGroup mb={4}>
                  <InputLeftElement>
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search queries by title, project, or description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    bg={inputBg}
                  />
                </InputGroup>

                {error && (
                  <Alert status="error" mb={4} borderRadius="md">
                    <AlertIcon />
                    <AlertTitle>Error Loading Data</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                    <Button size="sm" ml="auto" onClick={() => fetchQueries()}>
                      Retry
                    </Button>
                  </Alert>
                )}

                <Box overflowX="auto">
                  <Table variant="simple">
                    <Thead bg={tableHeaderBg}>
                      <Tr>
                        <Th color={textColor}>Title</Th>
                        <Th color={textColor}>Project</Th>
                        <Th color={textColor}>Status</Th>
                        <Th color={textColor}>Priority</Th>
                        <Th color={textColor}>Created</Th>
                        <Th color={textColor}>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {loading ? (
                        <Tr>
                          <Td colSpan={6} textAlign="center" py={8}>
                            <Spinner size="lg" />
                          </Td>
                        </Tr>
                      ) : filteredData.length === 0 ? (
                        <Tr>
                          <Td colSpan={6} textAlign="center" py={8}>
                            <Text color={subtleTextColor}>
                              No queries found. Click "New Query" to create one.
                            </Text>
                          </Td>
                        </Tr>
                      ) : (
                        filteredData.map((row) => (
                          <Tr
                            key={row._id}
                            _hover={{ bg: hoverBg }}
                            cursor="pointer"
                            onClick={() => handleViewQuery(row)}
                          >
                            <Td color={textColor} fontWeight="medium">
                              {row.title}
                            </Td>
                            <Td>
                              <Badge colorScheme="blue" variant="subtle">
                                {row.project}
                              </Badge>
                            </Td>
                            <Td>
                              <StatusPill status={row.status} />
                            </Td>
                            <Td>
                              <PriorityBadge priority={row.priority} />
                            </Td>
                            <Td color={textColor}>
                              {row.displayDate || row.created}
                            </Td>
                            <Td>
                              <HStack spacing={2}>
                                <IconButton
                                  icon={<ViewIcon />}
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewQuery(row);
                                  }}
                                  aria-label="View query"
                                />
                                <IconButton
                                  icon={<EditIcon />}
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setViewQuery(row);
                                    setIsEditing(true);
                                    onViewModalOpen();
                                  }}
                                  aria-label="Edit query"
                                />
                              </HStack>
                            </Td>
                          </Tr>
                        ))
                      )}
                    </Tbody>
                  </Table>
                </Box>
              </CardBody>
            </Card>
          </Box>

          <Box>
            <Card bg={cardBg} border="1px" borderColor={cardBorder} mb={6}>
              <CardBody>
                <Heading size="sm" mb={4} color={textColor}>
                  📞 Contact Support
                </Heading>
                <Text color={subtleTextColor} mb={4} fontSize="sm">
                  Need help? Reach out to our support team anytime.
                </Text>
                <VStack align="start" spacing={2}>
                  <Flex align="center" gap={2}>
                    <EmailIcon color={primaryColor} />
                    <Text fontSize="sm" color={textColor}>
                      support@techportal.com
                    </Text>
                  </Flex>
                  <Flex align="center" gap={2}>
                    <PhoneIcon color={primaryColor} />
                    <Text fontSize="sm" color={textColor}>
                      +1 (555) 123-4567
                    </Text>
                  </Flex>
                </VStack>
              </CardBody>
            </Card>

            <Card bg={cardBg} border="1px" borderColor={cardBorder}>
              <CardBody>
                <Heading size="sm" mb={4} color={textColor}>
                  Reports & Export
                </Heading>
                <Text color={subtleTextColor} mb={4} fontSize="sm">
                  Generate detailed reports and export data for analysis.
                </Text>

                <VStack spacing={4} align="stretch">
                  <Box>
                    <FormLabel fontSize="sm" color={textColor} mb={2}>
                      From Date
                    </FormLabel>
                    <Input
                      type="date"
                      value={reportFromDate}
                      onChange={(e) => setReportFromDate(e.target.value)}
                      bg={inputBg}
                      size="sm"
                    />
                  </Box>

                  <Box>
                    <FormLabel fontSize="sm" color={textColor} mb={2}>
                      To Date
                    </FormLabel>
                    <Input
                      type="date"
                      value={reportToDate}
                      onChange={(e) => setReportToDate(e.target.value)}
                      bg={inputBg}
                      size="sm"
                    />
                  </Box>

                  <Divider />

                  <Text fontSize="sm" fontWeight="medium" color={textColor}>
                    Export Format
                  </Text>

                  <VStack spacing={2}>
                    <Button
                      leftIcon={<FaFilePdf />}
                      onClick={downloadPDF}
                      w="full"
                      variant="outline"
                      size="sm"
                    >
                      Export as PDF
                    </Button>
                    <Button
                      leftIcon={<FaFileExcel />}
                      onClick={downloadExcel}
                      w="full"
                      variant="outline"
                      size="sm"
                    >
                      Export as Excel
                    </Button>
                    <Button
                      leftIcon={<FaFileWord />}
                      onClick={downloadWord}
                      w="full"
                      variant="outline"
                      size="sm"
                    >
                      Export as Word
                    </Button>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          </Box>
        </SimpleGrid>
      </Box>

      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalHeader color={textColor}>Create New Query</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isInvalid={!!formErrors.title}>
                <FormLabel color={textColor}>Title</FormLabel>
                <Input
                  value={formData.title}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                  placeholder="Enter query title"
                  bg={inputBg}
                />
              </FormControl>

              <FormControl isInvalid={!!formErrors.project}>
                <FormLabel color={textColor}>Project</FormLabel>
                <Select
                  value={formData.project}
                  onChange={(e) => handleFormChange('project', e.target.value)}
                  bg={inputBg}
                >
                  <option value="DMO Project">DMO Project</option>
                  <option value="WMC">WMC</option>
                  <option value="Bamboo">Bamboo</option>
                  <option value="ARMS">ARMS</option>
                  <option value="moli">moli</option>
                  <option value="TEST">TEST</option>
                  <option value="Ex employee">Ex employee</option>
                  <option value="real estate">real estate</option>
                  <option value="DMO">DMO</option>
                  <option value="Tech Support">Tech Support</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel color={textColor}>Priority</FormLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => handleFormChange('priority', e.target.value)}
                  bg={inputBg}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel color={textColor}>Description</FormLabel>
                <Textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, description: e.target.value }))
                  }
                  placeholder="Describe your query in detail..."
                  rows={4}
                  bg={inputBg}
                />
              </FormControl>

              <FormControl>
                <FormLabel color={textColor}>
                  Assigned To (comma-separated)
                </FormLabel>
                <Input
                  value={formData.assignedString}
                  onChange={(e) =>
                    handleFormChange('assignedString', e.target.value)
                  }
                  placeholder="e.g., John Doe, Jane Smith"
                  bg={inputBg}
                />
              </FormControl>

              <FormControl>
                <FormLabel color={textColor}>Attachment</FormLabel>
                <Input
                  type="file"
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0];
                    if (!f) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                      handleFormChange('uploadfile', reader.result);
                      handleFormChange('uploadfilename', f.name);
                    };
                    reader.readAsDataURL(f);
                  }}
                  bg={inputBg}
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => setShowAddModal(false)}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmitQuery}
              isLoading={submitting}
              loadingText="Submitting..."
            >
              Submit Query
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isViewModalOpen} onClose={onViewModalClose} size="2xl">
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalHeader color={textColor}>
            {isEditing ? 'Edit Query' : 'Query Details'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {viewQuery && (
              <VStack spacing={4} align="stretch">
                {isEditing ? (
                  <>
                    <FormControl>
                      <FormLabel color={textColor}>Title</FormLabel>
                      <Input
                        value={editForm?.title || ''}
                        onChange={(e) =>
                          setEditForm((f) => ({ ...f, title: e.target.value }))
                        }
                        bg={inputBg}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel color={textColor}>Project</FormLabel>
                      <Input
                        value={editForm?.project || ''}
                        onChange={(e) =>
                          setEditForm((f) => ({
                            ...f,
                            project: e.target.value,
                          }))
                        }
                        bg={inputBg}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel color={textColor}>Description</FormLabel>
                      <Textarea
                        value={editForm?.description || ''}
                        onChange={(e) =>
                          setEditForm((f) => ({
                            ...f,
                            description: e.target.value,
                          }))
                        }
                        rows={4}
                        bg={inputBg}
                      />
                    </FormControl>
                  </>
                ) : (
                  <>
                    <Box>
                      <Heading size="md" color={textColor} mb={2}>
                        {viewQuery.title}
                      </Heading>
                      <Flex gap={4} mb={4}>
                        <Badge colorScheme="blue" variant="subtle">
                          {viewQuery.project}
                        </Badge>
                        <StatusPill status={viewQuery.status} />
                        <PriorityBadge priority={viewQuery.priority} />
                      </Flex>
                    </Box>

                    <Box>
                      <Heading size="sm" color={textColor} mb={2}>
                        Description
                      </Heading>
                      <Text
                        color={textColor}
                        bg={inputBg}
                        p={4}
                        borderRadius="md"
                      >
                        {viewQuery.description || 'No description provided.'}
                      </Text>
                    </Box>

                    <SimpleGrid columns={2} spacing={4}>
                      <Box>
                        <Text color={subtleTextColor} fontSize="sm">
                          Created
                        </Text>
                        <Text color={textColor}>
                          {viewQuery.displayDate || viewQuery.created}
                        </Text>
                      </Box>
                      <Box>
                        <Text color={subtleTextColor} fontSize="sm">
                          Assigned To
                        </Text>
                        <Text color={textColor}>
                          {Array.isArray(viewQuery.assigned)
                            ? viewQuery.assigned.join(', ')
                            : viewQuery.assigned || 'Unassigned'}
                        </Text>
                      </Box>
                    </SimpleGrid>
                  </>
                )}
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <HStack spacing={3}>
              {isEditing ? (
                <>
                  <Button variant="ghost" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                  <Button colorScheme="green">Save Changes</Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditForm({ ...viewQuery });
                      setIsEditing(true);
                    }}
                  >
                    Edit
                  </Button>
                  <Button colorScheme="blue" onClick={onViewModalClose}>
                    Close
                  </Button>
                </>
              )}
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isKPIModalOpen} onClose={onKPIModalClose} size="4xl">
        <ModalOverlay />
        <ModalContent bg={cardBg} maxH="80vh">
          <ModalHeader color={textColor}>
            {selectedKPIModal === 'overall' &&
              `Overall Queries (${filteredKPIQueries.length})`}
            {selectedKPIModal === 'monthly' &&
              `Monthly Queries (${filteredKPIQueries.length})`}
            {selectedKPIModal === 'pending' &&
              `Pending Queries (${filteredKPIQueries.length})`}
            {selectedKPIModal === 'inProgress' &&
              `In Progress Queries (${filteredKPIQueries.length})`}
            {selectedKPIModal === 'resolved' &&
              `Resolved Queries (${filteredKPIQueries.length})`}
            {selectedKPIModal === 'rejected' &&
              `Rejected Queries (${filteredKPIQueries.length})`}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            <Table variant="simple">
              <Thead bg={tableHeaderBg} position="sticky" top={0}>
                <Tr>
                  <Th color={textColor}>#</Th>
                  <Th color={textColor}>Title</Th>
                  <Th color={textColor}>Project</Th>
                  <Th color={textColor}>Status</Th>
                  <Th color={textColor}>Priority</Th>
                  <Th color={textColor}>Created</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredKPIQueries.length === 0 ? (
                  <Tr>
                    <Td colSpan={6} textAlign="center" py={8}>
                      <Text color={subtleTextColor}>No queries found</Text>
                    </Td>
                  </Tr>
                ) : (
                  filteredKPIQueries.map((query, idx) => (
                    <Tr key={query._id || idx} _hover={{ bg: hoverBg }}>
                      <Td color={textColor} fontWeight="medium">
                        {idx + 1}
                      </Td>
                      <Td color={textColor}>{query.title}</Td>
                      <Td>
                        <Badge colorScheme="blue" variant="subtle">
                          {query.project}
                        </Badge>
                      </Td>
                      <Td>
                        <StatusPill status={query.status} />
                      </Td>
                      <Td>
                        <PriorityBadge priority={query.priority} />
                      </Td>
                      <Td color={textColor}>
                        {query.displayDate || query.created}
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onKPIModalClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export function QuerySummaryReport({ queries = [], onBack }) {
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const subtleTextColor = useColorModeValue('gray.600', 'gray.400');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');
  const toast = useToast();

  const exportPDF = async () => {
    if (!queries || queries.length === 0) {
      toast({
        title: 'No Data',
        description: 'No queries to export',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('Query Summary Report', 105, 15, { align: 'center' });

      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 15, 30);
      doc.text(`Total Queries: ${queries.length}`, 15, 40);

      const tableData = queries.map((q, idx) => [
        idx + 1,
        q.title || '',
        q.project || '',
        q.status || '',
        q.priority || '',
        q.created || '',
        Array.isArray(q.assigned) ? q.assigned.join(', ') : q.assigned || '',
      ]);

      doc.autoTable({
        head: [
          [
            '#',
            'Title',
            'Project',
            'Status',
            'Priority',
            'Created',
            'Assigned To',
          ],
        ],
        body: tableData,
        startY: 50,
        headStyles: {
          fillColor: [37, 99, 235],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
        },
      });

      doc.save(
        `Query-Summary-Report-${new Date().toISOString().slice(0, 10)}.pdf`,
      );
    } catch (error) {
      console.error('PDF export error:', error);
      toast({
        title: 'Export Error',
        description: 'Failed to export PDF',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const exportExcel = () => {
    if (!queries || queries.length === 0) {
      toast({
        title: 'No Data',
        description: 'No queries to export',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const ws_data = [
        ['Query Summary Report'],
        [`Generated on: ${new Date().toLocaleString()}`],
        [`Total Queries: ${queries.length}`],
        [],
        [
          '#',
          'Title',
          'Project',
          'Status',
          'Priority',
          'Created',
          'Assigned To',
        ],
        ...queries.map((q, idx) => [
          idx + 1,
          q.title || '',
          q.project || '',
          q.status || '',
          q.priority || '',
          q.created || '',
          Array.isArray(q.assigned) ? q.assigned.join(', ') : q.assigned || '',
        ]),
      ];

      const ws = XLSX.utils.aoa_to_sheet(ws_data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Queries');
      XLSX.writeFile(
        wb,
        `Query-Summary-Report-${new Date().toISOString().slice(0, 10)}.xlsx`,
      );
    } catch (error) {
      console.error('Excel export error:', error);
      toast({
        title: 'Export Error',
        description: 'Failed to export Excel',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const exportWord = () => {
    if (!queries || queries.length === 0) {
      toast({
        title: 'No Data',
        description: 'No queries to export',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      let content = 'Query Summary Report\n';
      content += `Generated on: ${new Date().toLocaleString()}\n`;
      content += `Total Queries: ${queries.length}\n\n`;
      content += '='.repeat(100) + '\n\n';

      queries.forEach((q, idx) => {
        content += `${idx + 1}. ${q.title}\n`;
        content += `   Project: ${q.project || 'N/A'}\n`;
        content += `   Status: ${q.status || 'N/A'}\n`;
        content += `   Priority: ${q.priority || 'N/A'}\n`;
        content += `   Created: ${q.created || 'N/A'}\n`;
        content += `   Assigned To: ${
          Array.isArray(q.assigned)
            ? q.assigned.join(', ')
            : q.assigned || 'N/A'
        }\n`;
        content += '-'.repeat(100) + '\n\n';
      });

      const blob = new Blob([content], {
        type: 'application/msword;charset=utf-8',
      });
      saveAs(
        blob,
        `Query-Summary-Report-${new Date().toISOString().slice(0, 10)}.doc`,
      );
    } catch (error) {
      console.error('Word export error:', error);
      toast({
        title: 'Export Error',
        description: 'Failed to export Word',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box bg={bgColor} minH="100vh" p={6}>
      <Box maxW="1200px" mx="auto">
        <Card bg={cardBg} shadow="lg">
          <CardBody p={6}>
            <Flex align="center" gap={4} mb={6}>
              <Button
                leftIcon={<ChevronLeftIcon />}
                variant="outline"
                onClick={onBack}
              >
                Back
              </Button>
              <Box>
                <Heading size="lg" color={textColor}>
                  Query Summary Report
                </Heading>
                <Text color={subtleTextColor}>
                  Download and manage all query reports
                </Text>
              </Box>
            </Flex>

            <Flex gap={3} mb={6} flexWrap="wrap">
              <Button
                leftIcon={<FaFilePdf />}
                colorScheme="blue"
                onClick={exportPDF}
              >
                Export PDF
              </Button>
              <Button
                leftIcon={<FaFileExcel />}
                colorScheme="green"
                onClick={exportExcel}
              >
                Export Excel
              </Button>
              <Button
                leftIcon={<FaFileWord />}
                colorScheme="purple"
                onClick={exportWord}
              >
                Export Word
              </Button>
            </Flex>

            <Box overflowX="auto">
              <Heading size="md" mb={4} color={textColor}>
                All Queries ({queries.length})
              </Heading>
              <Table variant="simple">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th color={textColor}>#</Th>
                    <Th color={textColor}>Title</Th>
                    <Th color={textColor}>Project</Th>
                    <Th color={textColor}>Status</Th>
                    <Th color={textColor}>Priority</Th>
                    <Th color={textColor}>Created</Th>
                    <Th color={textColor}>Assigned To</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {queries.length === 0 ? (
                    <Tr>
                      <Td colSpan={7} textAlign="center" py={8}>
                        <Text color={subtleTextColor}>No queries found</Text>
                      </Td>
                    </Tr>
                  ) : (
                    queries.map((q, idx) => (
                      <Tr key={idx}>
                        <Td color={textColor}>{idx + 1}</Td>
                        <Td color={textColor} fontWeight="medium">
                          {q.title}
                        </Td>
                        <Td>
                          <Badge colorScheme="blue" variant="subtle">
                            {q.project}
                          </Badge>
                        </Td>
                        <Td>
                          <StatusPill status={q.status} />
                        </Td>
                        <Td>
                          <PriorityBadge priority={q.priority} />
                        </Td>
                        <Td color={textColor}>{q.created}</Td>
                        <Td color={textColor}>
                          {Array.isArray(q.assigned)
                            ? q.assigned.join(', ')
                            : q.assigned || '—'}
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          </CardBody>
        </Card>
      </Box>
    </Box>
  );
}
